<template>
  <div class="main-content row mx-0">
    <!--NOTIFICATION MODAL -->
    <b-modal
      v-model="notificationModalBool"
      class="modal-content"
      :centered="true"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      ok-only
      :ok-variant="notificationProps.isWarning ? 't_red' : 't_green'"
      @ok="closeNotificationModal()"
      footer-class="justify-content-center"
      hide-header
    >
      <div class="modal-header">
        <button type="button" class="modal-close" @click="closeNotificationModal()">
          <i class="fas fa-lg fa-times"></i>
        </button>
      </div>
      <div class="text-center">
        <i :class="notificationIcon"></i>
        <h1>{{ notificationProps.message }}</h1>
      </div>
    </b-modal>
    <order-id-receipt
      v-if="showOrderIdReceipt"
      :orders="basketOrder"
      :showOrderHistoryLink="true"
      @hideOrderIdReceipt="hideReceiptModal"
    />
    <edit-order-details 
      v-if="viewEditOrdersDialog" 
      :orders="basketOrder" 
      :viewEditOrderDialog.sync="viewEditOrdersDialog" 
      :isConfirmingOrder="true"
      :isComingFromOrderCapture="isComingFromOrderCapture"
    />
    <confirmation-basket-refresh
      :show="confirmationBasketRefreshBool"
      @confirm-refresh="confirmBasketRefresh"
      @cancel-refresh="cancelBasketRefresh"
    />
    <create-price-quote
      :show="showCreateQuotePriceBool"
      @cancel-create="cancelCreatePriceQuote"
      @confirm-create="confirmCreatePriceQuote"
    />
    <div class="col-12 d-flex flex-row-reverse mb-2" :class="{ invisible: isBasketCreated }">
      <refresh-information />
    </div>
    <div :class="[$breakpoint.lgAndUp ? 'col-12 col-md-8' : 'col-12']">
      <h1 class="first-header heading-large mt-0"><i class="fas fa-file-invoice-dollar mr-2"></i> Order Capture</h1>
      <div class="panel-tabs">
        <div class="nav-tabs_parent">
          <ul class="nav nav-tabs nav-fill order-capture-tabs">
            <li class="nav-item" :class="{ active: getCurrentTab == tab.id }" v-for="(tab, key) in tabs" :key="key">
              <a class="nav-link" @click.prevent="selectTab(tab)" href="#"> {{ tab.name }} </a>
            </li>
          </ul>
        </div>
        <div class="panel-tab-content">
          <div class="card border-radius-top-left-0 border-radius-top-right-0">
            <div class="group-rounded-box my-3 py-2 mx-4">
              <!--SELECT VIEW-->
              <div class="title title-align-right">
                <span
                  @click="selectView($event)"
                  :id="views.priceQuotes"
                  :class="{ 'fade-text': currentView !== views.priceQuotes }"
                  class="custom-button"
                >
                  {{ views.priceQuotes }}
                </span>
                &bull;
                <span
                  @click="selectView($event)"
                  :id="views.offers"
                  :class="{ 'fade-text': currentView !== views.offers }"
                  class="custom-button"
                >
                  {{ views.offers }}
                </span>
              </div>

              <div v-if="currentView === views.offers">
                <select-boxes class="m-4" :isBasketCreated="isBasketCreated" @refresh-offers="refreshOffers" />
                <delivery-options
                  ref="deliveryOptions"
                  :customer="basketCustomer"
                  :initialDeliveredInData="initialDeliveredInData"
                  @updateDeliveryStatus="updateDeliveryStatus"
                  @updateDeliveredInData="updateDeliveredInData"
                  v-if="getSearchableBool && !isContract && !isNetworkSelected"
                />
                <offers
                  class="m-4"
                  :offersList="filteredOffers"
                  :allFiltersSelected="getSearchableBool"
                  :selectedCustomer="getCustomer"
                  :isNetworkSelected="isNetworkSelected"
                  :isServerError="getOffersServerErrorBool"
                  :isBasketCreated="isBasketCreated"
                  :loadingOffers="loadingOffers"
                  :haulageRate="effectiveHaulageRate"
                  :deliveryValidationError="deliveryValidationError"
                  @add-offer-to-basket="validateAndAddOfferToBasket"
                />
              </div>
              <div v-else>
                <price-quotes 
                  class="m-4"
                  @add-to-basket="addPriceQuoteToBasket"
                  ref="priceQuoteTable"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :class="[$breakpoint.lgAndUp ? 'col-12 col-md-4' : 'col-12']">
      <div :class="{ 'sticky-top': $breakpoint.lgAndUp }" style="z-index: 1">
        <h1 class="first-header heading-large mt-0">Basket</h1>
        <basket
          :basket="basket"
          :basketItems="basketItems"
          :customer="basketCustomer"
          :terminal="basketTerminal"
          :brands="brands"
          :basketTimeLimit="basketTimeLimit"
          :refreshBasketCountdown="mixin_time"
          :isPriceQuote="isPriceQuote"
          :triggerBasketErrorDisplay="triggerBasketErrorDisplay"
          :parentErrorMessage="basketErrorMessage"
          :isPlacingOrder="isPlacingOrder"
          @remove-item="removeOfferFromBasket"
          @update-volume="updateBasketItemOfferVolume"
          @update-delivery-date="updateDeliveryDate"
          @refresh-offers="refreshBasket"
          @save-price-quote="openCreatePriceQuoteModal"
          @place-order="buyOffer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import isSameDay from 'date-fns/isSameDay'
import breakpoint from "@/mixins/index";
import CreateBasketMixin from "@/mixins/CreateBasketMixin";
import TimerMixin from "@/mixins/TimerMixin";

import Offers from "@/components/OrderCapture/Offers.vue";
import PriceQuotes from "@/components/OrderCapture/PriceQuotes.vue";
import Basket from "@/components/OrderCapture/Basket.vue";
import SelectBoxes from "@/components/OrderCapture/SelectBoxes.vue";
import DeliveryOptions from "@/components/OrderCapture/DeliveryOptions.vue";
import RefreshInformation from "@/components/RefreshInformation.vue";

import ConfirmationBasketRefresh from "@/modals/ConfirmationBasketRefresh.vue";
import OrderIdReceipt from "@/modals/OrderIdReceipt.vue";
import CreatePriceQuote from "@/modals/CreatePriceQuote.vue";
import EditOrderDetails from "@/modals/EditOrderDetails.vue";

import { rulesEnum } from "@/util/enums/rules";
import { costAdditionScope } from "@/util/enums/costAdditionScope";
import { eBasketErrorMessages } from "@/util/enums/basket";

import { makeGenericContractFromContractFrequency } from "@/util/contracts";
import { filterPricingRules, getRuleScopeOfferExclusions, calculateCostAdditionPpl } from "@/util/pricingRules";
import { buildOrderCaptureBasketItemObject } from "@/util/basket";
import { organiseOrders } from "@/util/orderBuilder";
import {
  recommendedOfferQuery,
  calculateOrderItemPrice,
  calculateFinalOfferPriceAndMargin,
  calculateCurrentMaxAvailableVolWithBasket,
} from "@/util/offers.js";
import {
  getBioProductId,
  getBaseSettlementId,
  getStraightProductId,
  getStraightAndBlendedProductIds,
} from "@/util/products";

import { getRecommendedOffers } from "@/api/offers.js";
import { addAllItemsToApiBasket, createServerSideBasket, updateSelectedBrandInBasket, makeOfferBasketIntoDeliveredIn, updateBasketFields } from "@/api/basket";
import { placeOrderV2 } from "@/api/orders.js";
import { updatePriceQuoteBasketExpiry, createPriceQuote } from "@/api/priceQuotes";
import { getAccountInformation } from "@/api/users";

export default {
  data() {
    return {
      isComingFromOrderCapture: true,
      basket: null,
      basketTimeLimit: 600, // i.e. 10 minutes
      getOffersServerErrorBool: false,
      triggerBasketErrorDisplay: 0,
      basketErrorMessage: null,
      offers: [],
      priceQuotes: [],
      priceQuotesTerminal: null,
      priceQuotesCustomer: null,
      tabs: [
        {
          id: "SPOT",
          name: "Spot",
        },
        {
          id: "CONTRACT",
          name: "Contracts",
        },
      ],
      eNotificationProps: {
        priceQuoteSaved: {
          message: "Price Quote Saved",
          isConfirmation: true,
        },
      },
      views: {
        offers: "Offers",
        priceQuotes: "Price Quotes",
      },
      currentView: "",
      notificationProps: {},
      notificationModalBool: false,
      showCreateQuotePriceBool: false,
      showOrderIdReceipt: false,
      viewEditOrdersDialog: false,
      basketOrder: {},
      priceQuoteSaveData: {},
      confirmationBasketRefreshBool: false,
      resolveBasketRefresh: null,
      brands: [],
      lastOffersPromise: null,
      loadingOffers: false,
      costAdditionScopeGetter: costAdditionScope,
      isDeliveredIn: false,
      deliveredInData: null,
      deliveryValidationError: false,
      isPlacingOrder: false
    };
  },
  mixins: [breakpoint, CreateBasketMixin, TimerMixin],
  components: {
    SelectBoxes,
    DeliveryOptions,
    Offers,
    Basket,
    ConfirmationBasketRefresh,
    RefreshInformation,
    CreatePriceQuote,
    PriceQuotes,
    OrderIdReceipt,
    EditOrderDetails,
  },
  created: async function() {
    this.currentView = this.views.offers;
    if (this.getRoutePath !== this.$router.history.current.path) this.$router.push(this.getRoutePath);

    const accountInformation = await getAccountInformation(this.accountId);
    this.brands = accountInformation.brands;
  },
  computed: {
    ...mapGetters("login", ["getAccountId", "isPricingEngineEnabled", "isEditOrdersEnabled", "isUpdateOrderNumbersEnabled"]),
    ...mapGetters("terminals", ["getTerminalChanges"]),
    ...mapGetters("fuelsType", ["getFuelsType"]),
    ...mapGetters("accounts", ["customerList"]),
    ...mapGetters("livePrices", ["getPrices", "getFxSettlement", "marketData"]),
    ...mapGetters("orderCapture", [
      "getProducts",
      "getCustomer",
      "getNetworks",
      "getTerminal",
      "isNetworkSelected",
      "isContract",
      "getSearchableBool",
      "getCurrentTab",
      "getRoutePath",
    ]),
    ...mapGetters("metadata", ["blendedProductConfig", "productConfig", "networks", "staMatrix"]),
    filteredOffers: function () {
      return this.isBasketCreated ? this.filterOffersByLiftingPeriod(this.filterOffersByProductOrNetwork(), this.basket.offers[0].liftingPeriod) : this.offers;
    },
    spotOrContractPriceQuotes: function () {
      return this.priceQuotes.filter(({ basket }) => {
        const { contract } = basket.items[0];
        return this.isContract ? contract : !contract;
      }
      );
    },
    basketTerminal: function () {
      return this.isPriceQuote ? this.priceQuotesTerminal : this.getTerminal;
    },
    basketCustomer: function () {
      return this.isPriceQuote ? this.priceQuotesCustomer : this.getCustomer;
    },
    isBasketCreated: function () {
      return this.basket != null;
    },
    basketItems: function () {
      return this.basket && this.basket.offers ? this.basket.offers : [];
    },
    basketItemsLength: function() {
      return this.basketItems.length;
    },
    isPriceQuote: function () {
      return this.currentView === this.views.priceQuotes;
    },
    notificationIcon: function () {
      const { isConfirmation, isWarning } = this.notificationProps;
      if (isConfirmation) {
        return "fas fa-check-circle fa-5x text-t_green";
      } else if (isWarning) {
        return "fas fa-exclamation-circle fa-5x text-t_red";
      }
      return "fas fa-info-circle fa-5x";
    },
    effectiveHaulageRate: function () {
      return this.isDeliveredIn && this.deliveredInData && this.deliveredInData.haulageRate || 0;
    },
    // used to initialise the data in delivered in component when switching between tabs causing a reload
    initialDeliveredInData: function () {
      return this.deliveredInData ? {...this.deliveredInData, isDeliveredIn: this.isDeliveredIn} : null;
    }
  },
  watch: {
    getPrices: function () {
      if (!this.isBasketCreated) {
        this.refreshViewData();
      }
    },
    basketItemsLength: function () {
      this.clearBasketErrorMessage();
    },
    viewEditOrdersDialog: function() {
      if (!this.viewEditOrdersDialog) {
        this.refreshViewDataAndEmptyBasket();
      }
    },
    isDeliveredIn: function() {
      if (!this.isDeliveredIn) {
        this.deliveryValidationError = false;
      }
    }
  },
  methods: {
    ...mapActions("orderCapture", [
      "setCustomer",
      "setTerminal",
      "setProducts",
      "setNetworks",
      "setCurrentTab",
      "resetFilters",
    ]),
    ...mapActions("accounts", ["getRelationships"]),
    refreshOffers: async function () {
      this.loadingOffers = true;
      this.offers = await this.getLatestOffers();
      this.loadingOffers = false;
    },
    refreshPriceQuotes: async function () {
      if (this.$refs.priceQuoteTable) {
        this.$refs.priceQuoteTable.refreshPriceQuotes();
      }
    },
    getLatestOffers: async function() {
      /* 
        Ensures the offers corresponding to the current tab is returned
      */
      const offersPromise = this.fetchApiOffers();
      this.lastOffersPromise = offersPromise;
      return new Promise(async (res) => {
        const offers = await offersPromise;
        if (offersPromise === this.lastOffersPromise) {
          this.lastOffersPromise = null;
          res(offers);
        }
      })
    },
    fetchApiOffers: async function () {
      if (!this.getSearchableBool) {
        return [];
      }
      const offerQuery = {
        isContract: this.isContract,
        fuels: this.getFuelIds(this.getProducts, this.productConfig),
      };

      if (this.getNetworks.length > 0) {
        offerQuery.networks = this.getNetworks.map(({ _id }) => _id);
      }
      if (this.getTerminal) {
        offerQuery.terminalId = this.getTerminal._id;
      }

      const recommendedQuery = recommendedOfferQuery(this.getAccountId, this.getCustomer.id, offerQuery, true);

      let newOffers;
      try {
        const { body } = await getRecommendedOffers(recommendedQuery);
        newOffers = body;
        this.getOffersServerErrorBool = false;
      } catch (err) {
        this.getOffersServerErrorBool = true;
        newOffers = [];
      }
      return this.populateOrderCaptureOffers(newOffers, this.offers);
    },
    populateOrderCaptureOffers: function (newOffers, oldOffers) {
      return newOffers.map((offer) => {
        let oldOffer = oldOffers.find(({ _id }) => _id === offer._id);
        offer.adjustment = oldOffer && oldOffer.adjustment ? oldOffer.adjustment : null;
        offer.quantity = oldOffer && oldOffer.quantity ? oldOffer.quantity : null;
        offer.isAvailabilityDisabled = false;

        offer = makeGenericContractFromContractFrequency(offer);
        offer.currentMaximumAvailable = offer.maximumAvailable;

        return this.getOfferWithPricingBreakdown(offer);
      });
    },
    getOfferWithPricingBreakdown: function (offer) {
      const { fuel_id, pricingAuditTrail, customerMargin } = offer;

      if (offer.type_id === "SETTLEMENT") {
        let offerPricingBreakdown = {
          settleBasePrice: 0,
          liveBasePrice: 0,
          productMarketChange: 0,
          customerMargin: customerMargin || 0,
          costAdditions: offer.premium - (customerMargin || 0),
        };
        if (this.isPricingEngineEnabled && pricingAuditTrail) {
          const cumulativePricingRules = filterPricingRules(pricingAuditTrail, null, rulesEnum.eType.Cumulative);
          const customerMarginOfferExclusions = getRuleScopeOfferExclusions(
            this.costAdditionScopeGetter.Customer,
            this.costAdditionScopeGetter,
            rulesEnum.eOfferExclusion
          );
          const customerMarginPricingRules = filterPricingRules(
            pricingAuditTrail,
            null,
            rulesEnum.eType.Cumulative,
            customerMarginOfferExclusions
          );
          offerPricingBreakdown.auditTrail = {
            costAdditions: cumulativePricingRules.filter((rule) => !customerMarginPricingRules.find((r) => r === rule)),
            customerMargin: customerMarginPricingRules,
          };
          offerPricingBreakdown.auditTrail.costAdditions.forEach((rule) => {
            rule.action.valueInPpl = rule.action.value;
          });
          offerPricingBreakdown.auditTrail.customerMargin.forEach((rule) => {
            rule.action.valueInPpl = rule.action.value;
          });
        }
        offer.offerPricingBreakdown = offerPricingBreakdown;
        const { finalCustomerMargin, quotePrice, totalPrice } = calculateFinalOfferPriceAndMargin(offer);
        offer.finalCustomerMargin = finalCustomerMargin;
        offer.quotePrice = quotePrice;
        offer.totalPrice = totalPrice;
        return offer;
      }

      if (this.getPrices.length > 0) {
        // If we have live fuel prices, then populate the price breakdown
        // Populate the product's settle, live base prices, and market change
        const selectedProduct = this.getPrices.find((product) => product.fuel_id === fuel_id);
        let offerPricingBreakdown = {
          settleBasePrice: selectedProduct ? selectedProduct.totalFixed : 0,
          liveBasePrice: selectedProduct ? selectedProduct.totalLive : 0,
          productMarketChange: selectedProduct ? selectedProduct.totalLive - selectedProduct.totalFixed : 0,
        };

        const product = this.getPrices.find(product => product.fuel_id === fuel_id);
        
        // Populate its base product's price breakdown
        const straightProductId = getStraightProductId(this.productConfig, fuel_id);
        const baseSettlementProduct = this.getPrices.find(
          (product) => product.fuel_id === getBaseSettlementId(this.productConfig, straightProductId)
        );
        offerPricingBreakdown.baseProduct = {
          settlement: baseSettlementProduct ? baseSettlementProduct.price : 0,
          density: product.density,
          duty: product.duty,
        };

        // Populate its bio element's price breakdown, if the offer uses split quote (i.e. blended product)
        if (this.blendedProductConfig.includes(fuel_id)) {
          const bioElementId = getBioProductId(this.productConfig, fuel_id);
          const bioSettlementProduct = this.getPrices.find(
            (product) => product.fuel_id === getBaseSettlementId(this.productConfig, bioElementId)
          );
          offerPricingBreakdown.bioElement = {
            settlement: bioSettlementProduct ? bioSettlementProduct.price : 0,
            density: product.bioDensity,
            duty: product.bioDuty,
          };
        }

        // Get the customer margin and offer's premium
        if (this.isPricingEngineEnabled) {
          offerPricingBreakdown.customerMargin = customerMargin;
          offerPricingBreakdown.costAdditions = offer.premium - customerMargin;

          // Get audit trail based for normal cost additions and customer margins
          const cumulativePricingRules = filterPricingRules(pricingAuditTrail, null, rulesEnum.eType.Cumulative);
          const customerMarginOfferExclusions = getRuleScopeOfferExclusions(
            this.costAdditionScopeGetter.Customer,
            this.costAdditionScopeGetter,
            rulesEnum.eOfferExclusion
          );
          const customerMarginPricingRules = filterPricingRules(
            pricingAuditTrail,
            null,
            rulesEnum.eType.Cumulative,
            customerMarginOfferExclusions
          );
          offerPricingBreakdown.auditTrail = {
            costAdditions: cumulativePricingRules.filter((rule) => !customerMarginPricingRules.find((r) => r === rule)),
            customerMargin: customerMarginPricingRules,
          };

          // convert values of cost addition to their ppl equivalent
          offerPricingBreakdown.auditTrail.costAdditions.forEach((rule) => {
            const isBlendedProduct = this.blendedProductConfig.includes(fuel_id);
            rule.action.valueInPpl = calculateCostAdditionPpl(
              rule,
              selectedProduct,
              isBlendedProduct,
              this.getFxSettlement,
              this.marketData.ice.live,
              this.productConfig,
              this.staMatrix
            );
          });
          offerPricingBreakdown.auditTrail.customerMargin.forEach((rule) => {
            const isBlendedProduct = this.blendedProductConfig.includes(fuel_id);
            rule.action.valueInPpl = calculateCostAdditionPpl(
              rule,
              selectedProduct,
              isBlendedProduct,
              this.getFxSettlement,
              this.marketData.ice.live,
              this.productConfig,
              this.staMatrix
            );
          });
        } else {
          offerPricingBreakdown.customerMargin = this.getCustomer ? this.getCustomer.premium : 0;
        }
        offer.offerPricingBreakdown = offerPricingBreakdown;
      }
      const { finalCustomerMargin, quotePrice, totalPrice } = calculateFinalOfferPriceAndMargin(
        offer
      );

      offer.finalCustomerMargin = finalCustomerMargin;
      offer.quotePrice = quotePrice;
      offer.totalPrice = totalPrice;

      return offer;
    },
    getFuelIds: function (selectedProducts, productConfig) {
      const selectedProductIds = selectedProducts.flatMap(({ _id }) => {
        return getStraightAndBlendedProductIds(productConfig, _id);
      });
      return [...new Set(selectedProductIds)];
    },
    selectTab: function (selectedTab) {
      if (this.getCurrentTab !== selectedTab) {
        this.setCurrentTab(selectedTab.id);
        this.$router.push(this.getRoutePath);
        this.currentView = this.views.offers;
        this.refreshViewDataAndEmptyBasket();
      }
    },
    selectView: function (e) {
      const { id } = e.target;
      const prevView = this.currentView;
      this.currentView = id;

      if (prevView !== this.currentView) {
        this.refreshViewDataAndEmptyBasket();
      }
    },
    refreshViewData: function () {
      if (this.isPriceQuote) {
        this.refreshPriceQuotes();
      } else {
        this.refreshOffers();
      }
      this.refreshCustomerDetails();
    },
    refreshViewDataAndEmptyBasket: function () {
      this.emptyBasket();
      this.refreshViewData();
    },
    refreshCustomerDetails: async function () {
      await this.getRelationships();
      const customer = this.isPriceQuote ? this.priceQuotesCustomer : this.getCustomer;
      if (customer != null) {
        const updatedCustomerDetails = this.customerList.find(({ id }) => id === customer.id);

        if (this.isPriceQuote) {
          this.priceQuotesCustomer = updatedCustomerDetails;
        } else {
          this.setCustomer(updatedCustomerDetails);
        }
      }
    },
    updateOffersAvailabilityAndVolState: async function (currentOffers) {
      const apiOffers = await this.getLatestOffers();
      const latestOffers = [];
      for (const offer of currentOffers) {
        const matchingOffer = apiOffers.find(({ _id }) => _id === offer._id);
        if (matchingOffer != null) {
          const { minimumOrderQuantity, maximumAvailable } = matchingOffer;
          latestOffers.push({ ...offer, minimumOrderQuantity, maximumAvailable, isAvailabilityDisabled: false });
        } else {
          latestOffers.push({ ...offer, isAvailabilityDisabled: true });
        }
      }
      return latestOffers;
    },
    updateOffersCurrentMaxAvailable: function (orderCaptureOffers, basketItems) {
      return orderCaptureOffers.map((offer) => {
        const { _id, maximumAvailable } = offer;
        const currentMaximumAvailable = calculateCurrentMaxAvailableVolWithBasket(_id, maximumAvailable, basketItems);
        return { ...offer, currentMaximumAvailable, quantity: null };
      });
    },
    updateBasketItemsWithLatestOffers(basketItems, offers) {
      let updatedBasketItems = this.updateBasketItemsOfferAvailability(basketItems, offers);
      updatedBasketItems = this.updateBasketItemsMinMaxVolume(updatedBasketItems, offers);

      return updatedBasketItems;
    },
    updateBasketItemsOfferAvailability(basketItems, offers) {
      return basketItems.map((item) => {
        const matchingOffer = offers.find((o) => o._id === item._id);
        return { ...item, isOfferAvailable: matchingOffer != null && !matchingOffer.isAvailabilityDisabled };
      });
    },
    updateBasketItemsMinMaxVolume(basketItems, offers) {
      return basketItems.map((item) => {
        const matchingOffer = offers.find((o) => o._id === item._id);
        if (!matchingOffer || matchingOffer.isAvailabilityDisabled) {
          return { ...item };
        }
        const { maximumAvailable, currentMaximumAvailable, minimumOrderQuantity } = matchingOffer;
        return { ...item, maximumAvailable, currentMaximumAvailable, minimumOrderQuantity };
      });
    },
    updateBasketItemsPrice(basketItems, offers) {
      return basketItems.map((item) => {
        const matchingOffer = offers.find((o) => o._id === item._id);
        if (!matchingOffer) {
          return { ...item };
        }
        const { price } = matchingOffer;
        const { quotePrice, totalPrice } = calculateFinalOfferPriceAndMargin(
          { ...matchingOffer, adjustment: item.adjustment, quantity: item.quantity }
        );
        return { ...item, price, quotePrice, totalPrice };
      });
    },
    updateBasketItemOfferVolume: function (newQuantity, idx) {
      const { quantity: prevQuantity } = this.basket.offers[idx];
      if (prevQuantity !== newQuantity) {
        this.basket.offers[idx].quantity = newQuantity;
        this.offers = this.updateOffersCurrentMaxAvailable(this.offers, this.basket.offers);
        this.basket.offers = this.updateBasketItemsMinMaxVolume(this.basket.offers, this.offers);

        // Update each basket item's total price
        this.basket.offers = this.basket.offers.map((item) => {
          const totalPrice = calculateOrderItemPrice(item.quantity, item.quotePrice);
          return { ...item, totalPrice };
        });
      }
    },
    addPriceQuoteToBasket: async function (priceQuote) {
      const { account_id, _id: basketId } = priceQuote.basket;
      const { terminalId } = priceQuote.basket.items[0];

      const expiry = await updatePriceQuoteBasketExpiry(priceQuote._id);
      this.priceQuotesCustomer = this.customerList.find(({ id }) => id === account_id);
      this.priceQuotesTerminal = this.getTerminalChanges.find(({ _id }) => _id === terminalId);

      const { 
        basket: {
          items,
          brand,
          deliveryDate,
          haulageRate,
          location_id,
          customLiftingPeriod,
          orderReference,
        } 
      } = priceQuote;

      const offers = items.map((item) => {
        const {
          offerId,
          productId,
          quantity,
          networkId,
          terminalId,
          collectionTime,
          liftingPeriod,
          adjustment,
          fuelMeasurementId,
          price,
          pricingAuditTrail,
          offerReference,
          type_id,
        } = item;
        const quotePrice = Number(price.toFixed(2)) + Number(adjustment);
        const totalPrice = calculateOrderItemPrice(quantity, quotePrice);

        const basketItem = buildOrderCaptureBasketItemObject(
          offerId,
          productId,
          quantity,
          quotePrice,
          totalPrice,
          adjustment,
          fuelMeasurementId,
          networkId,
          terminalId,
          price,
          collectionTime,
          liftingPeriod,
          { _id: this.getAccountId },
          pricingAuditTrail,
          null,
          null,
          null,
          true,
          offerReference,
          type_id
        );
        if (item.contract) {
          basketItem.contract = { ...item.contract };
        }
        return basketItem;
      });
      let deliveredInData = null
      if (haulageRate || deliveryDate || location_id) {
        this.deliveredInData = {
          haulageRate,
          deliveryDate,
          location_id
        },
        deliveredInData = {...this.deliveredInData}
      }

      const payload = {
        offers,
        basketId,
        expiry,
        brand,
        deliveredInData,
        customLiftingPeriod,
        orderReference,
      };
      this.basket = this.populateOrderCaptureBasket(payload);

      // Get the latest corresponding set of offers for this basket to enable validation
      this.assignFiltersUsingPriceQuote(this.basket.offers, this.priceQuotesTerminal, this.priceQuotesCustomer);
      this.offers = await this.getLatestOffers();
      this.offers = this.updateOffersCurrentMaxAvailable(this.offers, this.basket.offers);
      this.basket.offers = this.updateBasketItemsOfferAvailability(this.basket.offers, this.offers);
      this.basket.offers = this.updateBasketItemsMinMaxVolume(this.basket.offers, this.offers);

      this.mixin_startTimer(this.basketTimeLimit);
    },
    validateAndAddOfferToBasket: function (offer, event) {
      if (this.isDeliveredIn) {
        this.$refs.deliveryOptions.validate((valid) => {
          if (valid) {
            this.addOfferToBasket(offer, event);
            this.deliveryValidationError = false;
          }
          else {
            this.deliveryValidationError = true;
          }
        })
        return;
      }
      this.addOfferToBasket(offer, event)
    },
    addOfferToBasket: async function (offer, event) {
      const {
        _id,
        fuel_id,
        quantity,
        quotePrice,
        totalPrice,
        networkId,
        terminal_id,
        collectionTime,
        liftingPeriod,
        adjustment,
        fuel_measurement_id,
        price,
        pricingAuditTrail,
        maximumAvailable,
        currentMaximumAvailable,
        minimumOrderQuantity,
      } = offer;

      const basketItem = buildOrderCaptureBasketItemObject(
        _id,
        fuel_id,
        quantity,
        quotePrice,
        totalPrice,
        adjustment,
        fuel_measurement_id,
        networkId,
        terminal_id,
        price,
        collectionTime,
        liftingPeriod,
        { _id: this.getAccountId },
        pricingAuditTrail,
        maximumAvailable,
        currentMaximumAvailable,
        minimumOrderQuantity,
        true,
        null,
        offer.type_id
      );

      if (offer.contract) {
        const { reference } = offer.contract;
        basketItem.contract = { reference };
      }

      const deliveredInData = this.isDeliveredIn ? this.deliveredInData : null;

      if (!this.isBasketCreated) {
        const initialText = event.srcElement.innerHTML;
        event.srcElement.disabled = true;
        event.srcElement.innerHTML = "Processing...";

        const apiBasket = await createServerSideBasket(this.marketWindowId, [basketItem], this.getCustomer.id);
        if (apiBasket.success) {
          apiBasket.offers = [basketItem];
          apiBasket.deliveredInData = deliveredInData;
    
          //Calling component needs a basket object to pass to the basket modal object
          this.basket = this.populateOrderCaptureBasket(apiBasket);
        } else {
          this.getOffersServerErrorBool = true;
          return;
        }

        event.srcElement.disabled = false;
        event.srcElement.innerHTML = initialText;
        this.mixin_startTimer(this.basketTimeLimit);
      } else {
        this.basket.offers.push(basketItem);
      }

      this.offers = this.updateOffersCurrentMaxAvailable(this.offers, this.basket.offers);
    },
    removeOfferFromBasket: function (idx) {
      this.basket.offers.splice(idx, 1);
      this.offers = this.updateOffersCurrentMaxAvailable(this.offers, this.basket.offers);
      if (this.basket.offers.length == 0) {
        this.refreshViewDataAndEmptyBasket();
      } else if (!this.isPriceQuote) {
        this.basket.offers = this.updateBasketItemsMinMaxVolume(this.basket.offers, this.offers);
      }
    },
    refreshBasket: async function (event) {
      event.srcElement.disabled = true;
      this.clearBasketErrorMessage();
      
      if (this.mixin_time > 0) {
        const isBasketRefresh = await this.openConfirmationBasketRefreshModal();
        if (!isBasketRefresh) {
          event.srcElement.disabled = false;
          return;
        }
      }

      if (this.isPriceQuote) {
        this.currentView = this.views.offers;
      }

      const basketResponse = await createServerSideBasket(this.marketWindowId, this.basket.offers, this.getCustomer.id);
      if (basketResponse.success) {
        this.basket.id = basketResponse.basketId;
        this.basket.basketExpiry = basketResponse.expiry;
        this.basket.basketCreated = new Date();
  
        let tempBasketOffers = await this.getLatestOffers();
        this.refreshCustomerDetails();
  
        tempBasketOffers = this.updateOffersCurrentMaxAvailable(tempBasketOffers, this.basket.offers);
        this.basket.offers = this.updateBasketItemsWithLatestOffers(this.basket.offers, tempBasketOffers);
        this.basket.offers = this.updateBasketItemsPrice(
          this.basket.offers,
          tempBasketOffers,
        );
        this.offers = tempBasketOffers;
        this.mixin_restartTimer();
      } else {
        this.basketErrorMessage = eBasketErrorMessages.BasketRefreshError;
        this.triggerBasketErrorDisplay++;
      }

      event.srcElement.disabled = false;
    },
    emptyBasket: function () {
      this.basket = null;
      this.priceQuotesTerminal = null;
      this.priceQuotesCustomer = null;
      this.mixin_stopTimer();
    },
    filterOffersByProductOrNetwork: function () {
      if (this.getNetworks.length > 0) {
        const networkIds = this.getNetworks.map(({ _id }) => _id);
        return this.offers.filter((offer) => {
          return networkIds.includes(offer.networkId);
        });
      }
      if (this.getProducts) {
        const productIds = this.getProducts.map(({ _id }) => _id);
        return this.offers.filter((offer) => {
          const straightProductId = getStraightProductId(this.productConfig, offer.fuel_id);
          return productIds.includes(straightProductId);
        });
      }
      return this.offers;
    },
    filterOffersByLiftingPeriod: function(offers, offerLiftingPeriod){
      if(this.isNetworkSelected){
        return offers;
      }
      if(!offerLiftingPeriod || offerLiftingPeriod.length < 2){
        return offers;
      }
      return offers.filter(({ liftingPeriod }) => {
        if (!liftingPeriod || liftingPeriod.length < 2) {
          return true;
        }
        const date1 = new Date(liftingPeriod[0]);
        const date2 = new Date(liftingPeriod[1]);
        const offerDate1 = new Date(offerLiftingPeriod[0]);
        const offerDate2 = new Date(offerLiftingPeriod[1]);

        return isSameDay(date1, offerDate1) && isSameDay(date2, offerDate2);
      })
    },
    assignFiltersUsingPriceQuote(priceQuoteBasketItems, priceQuoteTerminal, priceQuoteCustomer) {
      const productIds = [
        ...new Set(priceQuoteBasketItems.map(({ fuel_id }) => getStraightProductId(this.productConfig, fuel_id))),
      ];
      const products = productIds.map((id) => this.getFuelsType.find(({ _id }) => _id === id));

      const networkIds = [...new Set(priceQuoteBasketItems.map((item) => item.networkId).filter((id) => id != null))];
      const networks = networkIds.map((id) => this.networks.find(({ _id }) => _id === id));

      this.setCustomer(priceQuoteCustomer);
      this.setTerminal(priceQuoteTerminal);
      this.setNetworks(networks);
      this.setProducts(products);
    },
    buyOffer: async function (orderReference, liftingPeriod, brand, deliveredInData) {
      this.isPlacingOrder = true;
      try {
        await this.updateApiBasketWithLatestItems(this.basket.offers, this.basket.id, liftingPeriod);
        let clientReference;
        if (this.isContract) {
          clientReference = this.basket.offers.reduce(
            (acc, { contract }) => `${acc} ${contract.reference}${this.basket.offers.length > 1 ? ";" : ""}`,
            ""
          );
        } else {
          clientReference = orderReference;
        }
      
      if (deliveredInData) await makeOfferBasketIntoDeliveredIn(this.basket.id, deliveredInData);
      if(brand) await updateSelectedBrandInBasket({ id: this.basket.id, brand }, this.getAccountId);

        const payload = {
          basketId: this.basket.id,
          clientReference,
        };
        const placeOrderResponse = await placeOrderV2(payload);
        this.triggerOrderConfirmationModal(placeOrderResponse.data.order);
        this.clearBasketErrorMessage();
      } catch (ex) {
        this.handlePlaceOrderError(ex);
      }
      this.isPlacingOrder = false;
    },
    handlePlaceOrderError: async function (error) {
      // Default basket error message
      this.basketErrorMessage = eBasketErrorMessages.OrderRejectedError;
      
      if (error.response && error.response.status === 400) {
        /* 
        Update customer credit and offer details to trigger one of the following errors:
          1. customer credit error
          2. offer unavailable
          3. offer volume unavailable
        */
        await this.refreshCustomerDetails();
        this.offers = await this.updateOffersAvailabilityAndVolState(this.offers);
        this.offers = this.updateOffersCurrentMaxAvailable(this.offers, this.basket.offers);
        this.basket.offers = this.updateBasketItemsWithLatestOffers(this.basket.offers, this.offers);
      }
      this.triggerBasketErrorDisplay++;
    },
    updateApiBasketWithLatestItems: async function (basketItems, basketId, liftingPeriod) {
      const apiBasketResponse = await addAllItemsToApiBasket(basketItems, basketId, liftingPeriod);
      if (!apiBasketResponse.success) {
        throw apiBasketResponse.error;
      }
    },
    openConfirmationBasketRefreshModal: function () {
      this.confirmationBasketRefreshBool = true;
      return new Promise((res) => {
        this.resolveBasketRefresh = res;
      });
    },
    confirmBasketRefresh: function () {
      this.confirmationBasketRefreshBool = false;
      this.resolveBasketRefresh(true);
    },
    cancelBasketRefresh: function () {
      this.confirmationBasketRefreshBool = false;
      this.resolveBasketRefresh(false);
    },
    openCreatePriceQuoteModal(orderReference, liftingPeriod, selectedBrand, deliveredInData) {
      this.priceQuoteSaveData = {
        orderReference,
        liftingPeriod,
        selectedBrand,
        deliveredInData
      }
      this.showCreateQuotePriceBool = true;
    },
    confirmCreatePriceQuote: async function (expirationTimeInMinutes) {
      this.showCreateQuotePriceBool = false;
      const { priceQuoteSaved } = this.eNotificationProps;
      try {
        const {
          orderReference,
          liftingPeriod,
          selectedBrand,
          deliveredInData
        } = this.priceQuoteSaveData;

        const promisesArray = [
          addAllItemsToApiBasket(
            this.basket.offers, 
            this.basket.id
        )]

        if (deliveredInData) {
          promisesArray.push(makeOfferBasketIntoDeliveredIn(this.basket.id, deliveredInData));
        }

        if (orderReference || (liftingPeriod && liftingPeriod.length === 2)) {
          promisesArray.push(updateBasketFields(this.basket.id, { orderReference, customLiftingPeriod: liftingPeriod }));
        }

        if (selectedBrand) {
          promisesArray.push(updateSelectedBrandInBasket({ id: this.basket.id, brand: selectedBrand }, this.getAccountId));
        }

        await Promise.all(promisesArray);
        await createPriceQuote(this.basket.id, expirationTimeInMinutes, this.getCustomer.id, this.isContract);
        this.openNotificationModal(priceQuoteSaved);
      } catch (err) {
        this.basketErrorMessage = eBasketErrorMessages.CreatePriceQuoteError;
        this.triggerBasketErrorDisplay++;
      }
    },
    cancelCreatePriceQuote() {
      this.showCreateQuotePriceBool = false;
    },
    openNotificationModal(notificationProps) {
      this.notificationProps = notificationProps;
      this.notificationModalBool = true;
    },
    closeNotificationModal() {
      this.notificationModalBool = false;
      this.refreshViewDataAndEmptyBasket();
    },
    organiseOrder(order) {
      return organiseOrders(
        [order],
        this.$store.getters["login/getAccountType"],
        this.$store.getters["metadata/networks"]
      );
    },
    triggerOrderConfirmationModal(order) {
      this.basketOrder = this.organiseOrder(order);
      this.showOrderIdReceipt = true;
    },
    hideReceiptModal(confirmOrder) {
      this.showOrderIdReceipt = false;
      this.refreshViewDataAndEmptyBasket();
      if (confirmOrder && this.isEditOrdersEnabled) {
        this.viewEditOrdersDialog = true;
      }
    },
    clearBasketErrorMessage() {
      this.basketErrorMessage = null;
    },
    // used by Delivery panel to update data
    updateDeliveredInData(newDeliveredInData) {
      this.deliveredInData = {...newDeliveredInData};
      if (this.basket) {
        this.basket.deliveredInData = {...newDeliveredInData};
      }
    },
    updateDeliveryStatus(newDeliveryStatus) {
      this.isDeliveredIn = newDeliveryStatus;
      if (this.basket) {
        this.basket.deliveredInData = newDeliveryStatus ? {...this.deliveredInData} : null
      }
    },
    // used by basket panel to update deliverydate
    updateDeliveryDate(newDeliveryDate) {
      this.deliveredInData.deliveryDate = newDeliveryDate;
      if (this.$refs.deliveryOptions && this.$refs.deliveryOptions.deliveredInData) {
        this.$refs.deliveryOptions.deliveredInData.deliveryDate = newDeliveryDate;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.order-capture-tabs {
  @include media-breakpoint-down(md) {
    width: 100% !important;
  }
}

.title-align-right {
  position: relative !important;
  top: -18px !important;
  float: right;
}
.custom-button {
  cursor: pointer;
  padding: 0 2px 0 2px;
}
.fade-text {
  opacity: 0.3;
}
</style>
